import { RaidPlanEncounterAssignmentAbility } from 'app/frontend/types/custom/RaidPlan'
import {
  AssignmentScope,
  AssignmentViewMode,
  normalizeAssignmentBox,
  normalizeAssignmentCast,
} from './assignmentTimeline'
import getAssignmentEntityIdentity from './assignmentEntityIdentity'

type Input = {
  ability: RaidPlanEncounterAssignmentAbility
  scopes: AssignmentScope[]
  assignmentGroups: number
  viewMode: AssignmentViewMode
}

const countIdentityUsageAcrossBoxes = (
  boxesByScope: Record<string, ReturnType<typeof normalizeAssignmentBox>[]>,
) => {
  return Object.values(boxesByScope).reduce<Record<string, number>>(
    (acc, boxes) => {
      boxes.forEach((box) => {
        const identitiesInBox = new Set(
          box.entities
            .filter(
              (entity) =>
                entity.kind !== 'by_role' && entity.kind !== 'by_class',
            )
            .map((entity) => getAssignmentEntityIdentity(entity)),
        )
        identitiesInBox.forEach((identity) => {
          acc[identity] = (acc[identity] ?? 0) + 1
        })
      })
      return acc
    },
    {},
  )
}

const toDuplicateSet = (usageCountByIdentity: Record<string, number>) =>
  new Set(
    Object.entries(usageCountByIdentity)
      .filter(([, count]) => count > 1)
      .map(([identity]) => identity),
  )

export const getDuplicateIdentitiesByScopeId = ({
  ability,
  scopes,
  assignmentGroups,
  viewMode,
}: Input): Record<string, Set<string>> => {
  if (viewMode === 'all_separate') {
    return scopes.reduce<Record<string, Set<string>>>((acc, scope) => {
      acc[scope.id] = new Set<string>()
      return acc
    }, {})
  }

  const boxesByScope = scopes.reduce<
    Record<string, ReturnType<typeof normalizeAssignmentBox>[]>
  >((acc, scope) => {
    const representativeCast = normalizeAssignmentCast(
      ability.casts?.[scope.castIndexes[0]],
      assignmentGroups,
    )
    acc[scope.id] = Array.from({ length: assignmentGroups }, (_, boxIndex) =>
      normalizeAssignmentBox(representativeCast.boxes[boxIndex]),
    )
    return acc
  }, {})

  if (viewMode === 'alternating') {
    const duplicateIdentities = toDuplicateSet(
      countIdentityUsageAcrossBoxes(boxesByScope),
    )
    return scopes.reduce<Record<string, Set<string>>>((acc, scope) => {
      acc[scope.id] = duplicateIdentities
      return acc
    }, {})
  }

  return scopes.reduce<Record<string, Set<string>>>((acc, scope) => {
    const usageCountByIdentity = countIdentityUsageAcrossBoxes({
      [scope.id]: boxesByScope[scope.id],
    })
    acc[scope.id] = toDuplicateSet(usageCountByIdentity)
    return acc
  }, {})
}
